import { deepObjFind } from 'app/pages/loanApplication/LoanApplication.utils';
import _ from 'lodash';

import { ChecklistResultGenerationType, ChecklistSignalREventType } from 'constants/checklists';
import {
  LoanApplicationSignalREventType,
  LoanApplicationSignalRMessageType,
  LoanApplicationTabName,
  LoanDataChangedReason,
} from 'constants/loanApplication';
import { PAGE_CODES } from 'constants/pageCodes';
import { PRESENCE_REJOIN_GRACE_MS, PresenceSignalREventType } from 'constants/presence';
import { TaskSignalREventType } from 'constants/tasks';
import { checkIsAusSummaryRefreshRequired } from 'utils/aus';

import { getMasterPermissionsToPage, getUuid } from 'store/auth/selectors';
import { showMessageAction } from 'store/global/actions';
import {
  editTaskAction,
  getApplicationChangesHistoryAction,
  getApplicationChecklistsAction,
  getAssetsAction,
  getChecklistsExecutionSnapshotsAction,
  getFinancialInfoPropertiesAction,
  getFinancialInformationSummaryAction,
  getIncomesAction,
  getLiabilitiesAction,
  getLoanApplicationCommonDataByApplicationIdAction,
  getLoanApplicationHeaderViewAction,
  getLoanConditionByConditionIdAction,
  getLoanConditionsByIdsAction,
  getLoanApplicationTasksAction,
  getTaskSlaGroupsByApplicationIdAction,
  processAusSubmitOperationChangedAction,
  processAusSwitchStatusChangedAction,
  refreshAusSummaryAction,
  setActiveTabWithNavigationAction,
  setPricingStatusAction,
} from 'store/loanApplications/actions';
import { getApplicationChecklistByIdAction } from 'store/loanApplications/actions/checklists.actions';
import { remountActiveTabAction } from 'store/loanApplications/actions/controlUI.actions';
import { getLoanActionsForLoanAction } from 'store/loanApplications/actions/loanActions.actions';
import {
  selectConditionsWithPendingStatus,
  selectCurrentChecklistId,
  selectEntityReferenceLoadedApplicationId,
  selectIsAddConditionsPendingStatus,
  selectIsGetConditionsPendingStatus,
  selectTaskSlaGroup,
} from 'store/loanApplications/selectors';
import { selectLoanApplicationControlUI } from 'store/loanApplications/selectors/controlUI.selectors';
import {
  clearLocalEditedFieldsAction,
  clearRemoteFieldValuesAction,
  fieldLockAcquiredAction,
  fieldLockDeniedAction,
  fieldLockReleasedAction,
  fieldValueChangingAction,
  fieldValueCommittedAction,
  fieldValuesRevertedAction,
  setFollowNavigatingAction,
  setFollowNavigationInProgress,
  setPendingFieldValuesAction,
  setPresenceSnapshotAction,
  stopFollowingAction,
  userJoinedAction,
  userLeftAction,
  userLocationChangedAction,
  userStatusChangedAction,
} from 'store/presence/actions';
import {
  selectFollowedUser,
  selectFollowingUserId,
  selectPresenceUsers,
} from 'store/presence/selectors';

import { getApplicationLockedFields } from '../../../store/loanApplications/actions/lockedFields.actions';

import { createCoalescingRefetcher } from './coalescingRefetcher';

class SignalRLoanApplicationController {
  #connection;
  #dispatch;
  #getState;
  #featureToggles;
  #logger;
  #loanId;
  #applicationId;
  #actionsRefetcher;
  #headerRefetcher;
  #entityReferenceRefetcher;
  #ausSummaryRefetcher;
  #pendingFollowEndTimers = new Map();

  constructor(connection, logger) {
    this.#connection = connection;
    this.#logger = logger;
    this.#actionsRefetcher = createCoalescingRefetcher(() => this.#refetchActions());
    this.#headerRefetcher = createCoalescingRefetcher(() => this.#refetchHeader());
    this.#entityReferenceRefetcher = createCoalescingRefetcher(() =>
      this.#refetchEntityReferenceData(),
    );
    this.#ausSummaryRefetcher = createCoalescingRefetcher(() => this.#refetchAusSummary());
  }

  get connection() {
    if (!this.#connection) {
      this.#logger.error('Trying to use connection without active connection');
    }

    return this.#connection;
  }

  init(dispatch, featureToggles, getState) {
    if (!dispatch) {
      this.#logger.error('Dispatch is required for initializing signalR loan editor controller');
    }

    this.#dispatch = dispatch;
    this.#featureToggles = featureToggles;
    this.#getState = getState;
  }

  onPageOpened(loanId, applicationId, companyId) {
    this.#connection.invoke(
      LoanApplicationSignalRMessageType.CONNECT_TO_LOAN_EDITOR,
      loanId,
      companyId,
    );
    this.#loanId = loanId;
    this.#applicationId = applicationId;
    this.#startListeners();
  }

  onPageClosed(loanId, companyId) {
    this.#stopListeners();
    this.#loanId = null;
    this.#applicationId = null;

    return this.#connection.invoke(
      LoanApplicationSignalRMessageType.DISCONNECT_FROM_LOAN_EDITOR,
      loanId,
      companyId,
    );
  }

  #startListeners() {
    this.connection.on(
      LoanApplicationSignalREventType.AUS_SUBMIT_OPERATION_CHANGED,
      this.#onAusSubmitOperationChanged.bind(this),
    );

    this.connection.on(
      LoanApplicationSignalREventType.AUS_TYPE_SWITCH_STATUS_CHANGED,
      this.#onAusTypeSwitchStatusChanged.bind(this),
    );

    this.connection.on(
      TaskSignalREventType.TASK_STATUS_CHANGED,
      this.#onTaskStatusUpdate.bind(this),
    );

    this.connection.on(
      ChecklistSignalREventType.CHECKLIST_RESULTS_CHANGED,
      this.#onChecklistResultsChanged.bind(this),
    );

    this.connection.on(
      ChecklistSignalREventType.CHECKLIST_EXECUTION_SNAPSHOTS_CHANGED,
      this.#onChecklistExecutionSnapshotsChanged.bind(this),
    );

    this.connection.on(
      LoanApplicationSignalREventType.MESSAGE_RAISED,
      this.#onMessageRaised.bind(this),
    );

    this.connection.on(
      LoanApplicationSignalREventType.CONDITIONS_MODIFIED,
      this.#onConditionsModified.bind(this),
    );

    this.connection.on(
      LoanApplicationSignalREventType.CONDITION_CREATED,
      this.#onConditionCreated.bind(this),
    );

    this.connection.on(
      LoanApplicationSignalREventType.AUTO_LOCKED_FIELDS_CHANGED,
      this.#onAutoLockedFieldsChanged.bind(this),
    );

    if (this.#featureToggles?.isExternalLoanLockEnabled) {
      this.connection.on(
        LoanApplicationSignalREventType.PRICING_STATUS_CHANGED,
        this.#onPricingStatusChanged.bind(this),
      );

      this.connection.on(
        LoanApplicationSignalREventType.PENDING_CHANGES_SAVED,
        this.#onPendingChangesSaved.bind(this),
      );
    }

    this.connection.on(
      PresenceSignalREventType.PRESENCE_SNAPSHOT,
      this.#onPresenceSnapshot.bind(this),
    );

    this.connection.on(
      PresenceSignalREventType.USER_JOINED_LOAN,
      this.#onUserJoinedLoan.bind(this),
    );

    this.connection.on(PresenceSignalREventType.USER_LEFT_LOAN, this.#onUserLeftLoan.bind(this));

    this.connection.on(
      PresenceSignalREventType.USER_LOCATION_CHANGED,
      this.#onUserLocationChanged.bind(this),
    );

    this.connection.on(
      PresenceSignalREventType.USER_STATUS_CHANGED,
      this.#onUserStatusChanged.bind(this),
    );

    this.connection.on(
      PresenceSignalREventType.FIELD_LOCK_ACQUIRED,
      this.#onFieldLockAcquired.bind(this),
    );

    this.connection.on(
      PresenceSignalREventType.FIELD_LOCK_RELEASED,
      this.#onFieldLockReleased.bind(this),
    );

    this.connection.on(
      PresenceSignalREventType.FIELD_LOCK_DENIED,
      this.#onFieldLockDenied.bind(this),
    );

    this.connection.on(
      PresenceSignalREventType.FIELD_VALUE_CHANGING,
      this.#onFieldValueChanging.bind(this),
    );

    this.connection.on(
      PresenceSignalREventType.FIELD_VALUE_COMMITTED,
      this.#onFieldValueCommitted.bind(this),
    );

    this.connection.on(
      PresenceSignalREventType.FIELD_VALUES_REVERTED,
      this.#onFieldValuesReverted.bind(this),
    );

    this.connection.on(
      PresenceSignalREventType.PENDING_FIELD_VALUES,
      this.#onPendingFieldValues.bind(this),
    );

    this.connection.on(
      LoanApplicationSignalREventType.LOAN_DATA_CHANGED,
      this.#onLoanDataChanged.bind(this),
    );

    this.connection.on(
      LoanApplicationSignalREventType.LOAN_HEADER_REFRESH_REQUESTED,
      this.#onLoanHeaderRefreshRequested.bind(this),
    );
  }

  #stopListeners() {
    this.connection.off(LoanApplicationSignalREventType.AUS_SUBMIT_OPERATION_CHANGED);
    this.connection.off(LoanApplicationSignalREventType.AUS_TYPE_SWITCH_STATUS_CHANGED);

    this.connection.off(TaskSignalREventType.TASK_STATUS_CHANGED);

    this.connection.off(LoanApplicationSignalREventType.MESSAGE_RAISED);
    this.connection.off(LoanApplicationSignalREventType.CONDITIONS_MODIFIED);
    this.connection.off(LoanApplicationSignalREventType.CONDITION_CREATED);
    this.connection.off(LoanApplicationSignalREventType.AUTO_LOCKED_FIELDS_CHANGED);

    if (this.#featureToggles?.isExternalLoanLockEnabled) {
      this.connection.off(LoanApplicationSignalREventType.PRICING_STATUS_CHANGED);
      this.connection.off(LoanApplicationSignalREventType.PENDING_CHANGES_SAVED);
    }

    this.connection.off(PresenceSignalREventType.PRESENCE_SNAPSHOT);
    this.connection.off(PresenceSignalREventType.USER_JOINED_LOAN);
    this.connection.off(PresenceSignalREventType.USER_LEFT_LOAN);
    this.connection.off(PresenceSignalREventType.USER_LOCATION_CHANGED);
    this.connection.off(PresenceSignalREventType.USER_STATUS_CHANGED);
    this.connection.off(PresenceSignalREventType.FIELD_LOCK_ACQUIRED);
    this.connection.off(PresenceSignalREventType.FIELD_LOCK_RELEASED);
    this.connection.off(PresenceSignalREventType.FIELD_LOCK_DENIED);
    this.connection.off(PresenceSignalREventType.FIELD_VALUE_CHANGING);
    this.connection.off(PresenceSignalREventType.FIELD_VALUE_COMMITTED);
    this.connection.off(PresenceSignalREventType.FIELD_VALUES_REVERTED);
    this.connection.off(PresenceSignalREventType.PENDING_FIELD_VALUES);
    this.connection.off(LoanApplicationSignalREventType.LOAN_DATA_CHANGED);
    this.connection.off(LoanApplicationSignalREventType.LOAN_HEADER_REFRESH_REQUESTED);

    this.#actionsRefetcher.cancel();
    this.#headerRefetcher.cancel();
    this.#entityReferenceRefetcher.cancel();
    this.#ausSummaryRefetcher.cancel();
    this.#clearAllPendingFollowEndTimers();
  }

  #onAusSubmitOperationChanged(data) {
    this.#dispatch(processAusSubmitOperationChangedAction(data));

    if (checkIsAusSummaryRefreshRequired(data?.status)) {
      this.#ausSummaryRefetcher.request();
    }
  }

  #onAusTypeSwitchStatusChanged(data) {
    this.#dispatch(processAusSwitchStatusChangedAction(data));
  }

  #getConditionsState() {
    const state = this.#getState?.();

    const isGetConditionsPendingStatus = selectIsGetConditionsPendingStatus(state);
    const isAddConditionsPendingStatus = selectIsAddConditionsPendingStatus(state);
    const conditionsWithPendingStatus = selectConditionsWithPendingStatus(state);

    return {
      isGetConditionsPendingStatus,
      isAddConditionsPendingStatus,
      conditionsWithPendingStatus,
    };
  }

  #getCurrentChecklistId() {
    const state = this.#getState?.();

    return selectCurrentChecklistId(state);
  }

  #onConditionCreated(loanConditionId) {
    const { isGetConditionsPendingStatus, isAddConditionsPendingStatus } =
      this.#getConditionsState();

    if (isGetConditionsPendingStatus || isAddConditionsPendingStatus) {
      return;
    }

    this.#dispatch(getLoanConditionByConditionIdAction({ loanConditionId }));
    this.#dispatch(getLoanApplicationTasksAction({ applicationId: this.#applicationId }));
  }

  #onConditionsModified(loanConditionIds) {
    const {
      isGetConditionsPendingStatus,
      isAddConditionsPendingStatus,
      conditionsWithPendingStatus,
    } = this.#getConditionsState();

    if (isGetConditionsPendingStatus || isAddConditionsPendingStatus) {
      return;
    }

    const filteredLoanConditionIds = _.without(loanConditionIds, ...conditionsWithPendingStatus);

    if (!filteredLoanConditionIds.length) {
      return;
    }

    this.#dispatch(getLoanConditionsByIdsAction({ loanConditionId: filteredLoanConditionIds }));
    this.#dispatch(getLoanApplicationTasksAction({ applicationId: this.#applicationId }));
  }

  #onTaskStatusUpdate(taskData) {
    this.#dispatch(editTaskAction(taskData, { withPartialData: true }));

    const { slaGroupId } = taskData;

    if (_.isNil(slaGroupId) || _.has(selectTaskSlaGroup(this.#getState?.()), slaGroupId)) {
      return;
    }

    this.#dispatch(getTaskSlaGroupsByApplicationIdAction({ applicationId: this.#applicationId }));
  }

  #onAutoLockedFieldsChanged() {
    this.#dispatch(getApplicationLockedFields({ applicationId: this.#applicationId }));
  }

  #onChecklistResultsChanged(changedData) {
    const state = this.#getState?.();

    const { isViewAllowed } = getMasterPermissionsToPage(PAGE_CODES.LOAN_CHECKLISTS)(state);

    if (!isViewAllowed) {
      return;
    }

    switch (changedData.type) {
      case ChecklistResultGenerationType.FOR_APPLICATION: {
        const { applicationId } = changedData;

        if (applicationId === this.#applicationId) {
          this.#dispatch(getApplicationChecklistsAction({ applicationId }));
        }

        break;
      }

      case ChecklistResultGenerationType.FOR_CHECKLIST:
        this.#dispatch(getApplicationChecklistsAction({ applicationId: this.#applicationId }));
        break;

      default:
        break;
    }
  }

  #onChecklistExecutionSnapshotsChanged(changedData) {
    // The execution-snapshot slice drives the loan-editor completeness "Needed" indicators
    // (DEV-4383), a loan-scoped read, so it must refresh for every user regardless of the
    // LOAN_CHECKLISTS admin-page view permission (DEV-4388).
    this.#dispatch(
      getChecklistsExecutionSnapshotsAction({
        applicationId: this.#applicationId,
        includeResults: true,
        includeItems: true,
      }),
    );

    const state = this.#getState?.();

    const { isViewAllowed } = getMasterPermissionsToPage(PAGE_CODES.LOAN_CHECKLISTS)(state);

    // The open-checklist detail (with pending changes) below is checklists-tab data and stays
    // gated on the LOAN_CHECKLISTS view permission.
    if (!isViewAllowed) {
      return;
    }

    const currentChecklistId = this.#getCurrentChecklistId();

    if (!currentChecklistId) {
      return;
    }

    const currentChecklistChanges = changedData.filter(
      target => target.checklistId === currentChecklistId,
    );

    if (currentChecklistChanges.length) {
      this.#dispatch(
        getApplicationChecklistByIdAction({
          applicationId: this.#applicationId,
          includeResults: true,
          includeItems: true,
          includePendingChanges: true,
          checklistId: currentChecklistId,
        }),
      );
    }
  }

  #onMessageRaised({ message, type }) {
    const formattedVariant = type.toLowerCase();

    this.#dispatch(showMessageAction({ message, variant: formattedVariant }));
  }

  #onPricingStatusChanged(data) {
    const signalApplicationId = data?.applicationId;

    if (signalApplicationId && signalApplicationId !== this.#applicationId) {
      return;
    }

    this.#dispatch(setPricingStatusAction(data));
  }

  #onPendingChangesSaved(_, signalApplicationId) {
    if (
      !this.#applicationId ||
      (signalApplicationId && signalApplicationId !== this.#applicationId)
    ) {
      return;
    }

    // Wrapped in Promise.resolve so this tolerates a dispatch mock that returns a plain action
    // (as opposed to redux-thunk's real promise-returning dispatch) — the broker's expected 403
    // here must never surface as an unhandled rejection (DEV-4226).
    Promise.resolve(
      this.#dispatch(getApplicationChangesHistoryAction({ applicationId: this.#applicationId })),
    ).catch(Error);
    this.#dispatch(
      getLoanApplicationCommonDataByApplicationIdAction({ applicationId: this.#applicationId }),
    );
  }

  #onPresenceSnapshot(data) {
    this.#dispatch(
      setPresenceSnapshotAction({
        ...data,
        currentConnectionId: this.#connection.connectionId,
      }),
    );
  }

  #onUserJoinedLoan(data) {
    const currentUserId = getUuid(this.#getState?.());
    const isSelfConnection = data?.userId === currentUserId;

    this.#dispatch(userJoinedAction({ ...data, isSelfConnection }));
    this.#cancelPendingFollowEndTimer(data?.userId);
  }

  #cancelPendingFollowEndTimer(userId) {
    const pendingTimer = this.#pendingFollowEndTimers.get(userId);

    if (_.isNil(pendingTimer)) {
      return;
    }

    clearTimeout(pendingTimer);
    this.#pendingFollowEndTimers.delete(userId);
  }

  #clearAllPendingFollowEndTimers() {
    this.#pendingFollowEndTimers.forEach(timer => clearTimeout(timer));
    this.#pendingFollowEndTimers.clear();
  }

  #endFollowForDeparture(data) {
    this.#dispatch(stopFollowingAction());
    this.#dispatch(
      showMessageAction({
        message: `${data.displayName ?? 'User'} left the loan. Follow mode ended.`,
        variant: 'info',
      }),
    );
  }

  #onUserLeftLoan(data) {
    const currentUserId = getUuid(this.#getState?.());
    const isSelfConnection = data?.userId === currentUserId;

    this.#dispatch(userLeftAction({ ...data, isSelfConnection }));

    if (isSelfConnection) {
      return;
    }

    const state = this.#getState?.();
    const followingUserId = selectFollowingUserId(state);

    if (data?.userId !== followingUserId) {
      return;
    }

    const hasRemainingConnectionsForUser = selectPresenceUsers(state).some(
      user => user.userId === data.userId,
    );

    if (hasRemainingConnectionsForUser) {
      return;
    }

    this.#cancelPendingFollowEndTimer(data.userId);
    this.#pendingFollowEndTimers.set(
      data.userId,
      setTimeout(() => {
        this.#pendingFollowEndTimers.delete(data.userId);
        this.#endFollowForDeparture(data);
      }, PRESENCE_REJOIN_GRACE_MS),
    );
  }

  #onUserLocationChanged(data) {
    const stateBefore = this.#getState?.();
    const followingUserId = selectFollowingUserId(stateBefore);
    const followedUser = selectFollowedUser(stateBefore);

    this.#dispatch(userLocationChangedAction(data));

    if (_.isNil(followingUserId) || data?.userId !== followingUserId) {
      return;
    }

    const state = this.#getState?.();
    const updatedFollowedUser = selectFollowedUser(state);

    const hasLocationChanged =
      updatedFollowedUser?.tab !== followedUser?.tab ||
      updatedFollowedUser?.entityId !== followedUser?.entityId ||
      updatedFollowedUser?.sectionId !== followedUser?.sectionId ||
      updatedFollowedUser?.subtab !== followedUser?.subtab ||
      updatedFollowedUser?.subsection !== followedUser?.subsection;

    if (hasLocationChanged && !_.isNil(updatedFollowedUser?.tab)) {
      this.#onFollowedUserLocationChanged(data);
    }

    if (!_.isNil(data.scrollTop)) {
      this.#onFollowedUserScrollChanged(data.scrollTop);
    }
  }

  #onFollowedUserLocationChanged() {
    const currentState = this.#getState?.();
    const currentFollowedUser = selectFollowedUser(currentState);

    const targetTabName = currentFollowedUser?.tab;
    const tabKey = _.invert(LoanApplicationTabName)[targetTabName];

    if (!_.isNil(tabKey)) {
      const { availableTabs } = selectLoanApplicationControlUI(currentState);

      if (!deepObjFind(availableTabs, tabKey)) {
        this.#dispatch(stopFollowingAction());
        this.#dispatch(
          showMessageAction({
            message: `Cannot follow — you don't have access to ${targetTabName}`,
            variant: 'info',
          }),
        );

        return;
      }
    }

    setFollowNavigationInProgress(true);
    this.#dispatch(setFollowNavigatingAction(true));
    this.#dispatch(
      setActiveTabWithNavigationAction({
        activeTab: targetTabName,
        activeSubtab: currentFollowedUser?.subtab,
        loanId: this.#loanId,
        applicationId: this.#applicationId,
        entityId: currentFollowedUser?.entityId,
        section: currentFollowedUser?.sectionId,
        subsection: currentFollowedUser?.subsection,
      }),
    );
    setFollowNavigationInProgress(false);
  }

  #onFollowedUserScrollChanged(scrollTop) {
    const scrollContainer = document.querySelector('main');

    if (scrollContainer) {
      scrollContainer.scrollTop = scrollTop;
    }
  }

  #onUserStatusChanged(data) {
    this.#dispatch(userStatusChangedAction(data));
  }

  #onFieldLockAcquired(data) {
    this.#dispatch(fieldLockAcquiredAction(data));
  }

  #onFieldLockReleased(data) {
    this.#dispatch(fieldLockReleasedAction(data));
  }

  #onFieldLockDenied(data) {
    this.#dispatch(fieldLockDeniedAction(data));
    this.#dispatch(
      fieldLockAcquiredAction({
        fieldPath: data?.fieldPath,
        userId: data?.heldByUserId,
        displayName: data?.heldByDisplayName,
        connectionId: data?.connectionId,
      }),
    );
    this.#dispatch(
      showMessageAction({
        message: `This field is being edited by ${data?.heldByDisplayName || 'another user'}`,
        variant: 'warning',
      }),
    );

    document.activeElement?.blur();
  }

  #onFieldValueChanging(data) {
    this.#dispatch(fieldValueChangingAction(data));
  }

  #onFieldValueCommitted(data) {
    this.#dispatch(fieldValueCommittedAction(data));
  }

  #onFieldValuesReverted(data) {
    this.#dispatch(fieldValuesRevertedAction(data));
  }

  #onPendingFieldValues(data) {
    this.#dispatch(setPendingFieldValuesAction(data));
  }

  async #onLoanDataChanged(data) {
    if (!this.#applicationId || data?.applicationId !== this.#applicationId) {
      return;
    }

    const isCancelled = data?.reason === LoanDataChangedReason.PENDING_CHANGES_CANCELLED;

    this.#dispatch(clearRemoteFieldValuesAction({ reason: isCancelled ? 'cancel' : 'save' }));
    await this.#dispatch(
      getLoanApplicationCommonDataByApplicationIdAction({ applicationId: this.#applicationId }),
    );
    // Do NOT pass triggeredByPendingChanges — this broadcast hits ALL users on the loan.
    // Restyled tabs (Property & Loan, Borrowers, etc.) already pick up fresh data via
    // selectors after the re-fetch above, so they must NOT be force-remounted (which
    // would close any open drawer on the remote user's screen).
    // Legacy tabs still remount because they are not in tabsWithoutRemountOnRefetch.
    this.#dispatch(remountActiveTabAction());

    if (data?.reason === LoanDataChangedReason.PENDING_CHANGES_SAVED) {
      // See the Promise.resolve note in #onPendingChangesSaved above (DEV-4226).
      Promise.resolve(
        this.#dispatch(getApplicationChangesHistoryAction({ applicationId: this.#applicationId })),
      ).catch(Error);
    }

    if (isCancelled) {
      this.#dispatch(clearLocalEditedFieldsAction());
    }

    // Refresh loan actions (toolbar) so all users see the updated pending changes state.
    this.#dispatch(getLoanActionsForLoanAction({ applicationId: this.#applicationId }));

    // Keep the chat entity-reference chips/badges live: a loan-data change (a borrower name, an
    // income amount, etc.) must reflow the candidate labels the chips read from. Coalesced +
    // self-gated on "already loaded for this loan" (see #refetchEntityReferenceData) so a viewer
    // who never opened the picker doesn't pay 5 extra fetches on every broadcast.
    this.#entityReferenceRefetcher.request();
  }

  #onLoanHeaderRefreshRequested(flags) {
    if (!this.#applicationId) {
      return;
    }

    if (flags?.actionsRefreshRequired) {
      this.#actionsRefetcher.request();
    }

    if (flags?.headerRefreshRequired) {
      this.#headerRefetcher.request();
    }
  }

  #refetchActions() {
    return this.#dispatch(getLoanActionsForLoanAction({ applicationId: this.#applicationId }));
  }

  #refetchEntityReferenceData() {
    const state = this.#getState?.();
    const loadedApplicationId = selectEntityReferenceLoadedApplicationId(state);

    // Skip when the entity-reference (financialInfo) slice was never loaded for THIS loan — the
    // chips only exist for a viewer who opened the picker/chips, so refetching for anyone else
    // is wasted work (and would prime a slice they never read from).
    if (
      !this.#applicationId ||
      loadedApplicationId?.toLowerCase() !== this.#applicationId.toLowerCase()
    ) {
      return;
    }

    this.#dispatch(getAssetsAction({ applicationId: this.#applicationId }));
    this.#dispatch(getLiabilitiesAction({ applicationId: this.#applicationId }));
    this.#dispatch(getIncomesAction({ applicationId: this.#applicationId }));
    this.#dispatch(getFinancialInfoPropertiesAction({ applicationId: this.#applicationId }));
    this.#dispatch(getFinancialInformationSummaryAction({ applicationId: this.#applicationId }));
  }

  #refetchAusSummary() {
    if (!this.#loanId) {
      return;
    }

    return this.#dispatch(refreshAusSummaryAction({ loanId: this.#loanId }));
  }

  #refetchHeader() {
    return Promise.allSettled([
      this.#dispatch(getLoanApplicationHeaderViewAction({ applicationId: this.#applicationId })),
      this.#dispatch(
        getLoanApplicationCommonDataByApplicationIdAction({ applicationId: this.#applicationId }),
      ),
    ]);
  }
}

export default SignalRLoanApplicationController;
